/**
 * ================================================================================================
 *  ABOUT SYSTEM — WHAT IS ACTUALLY RUNNING, OR AN HONEST ADMISSION THAT WE DO NOT KNOW.
 * ================================================================================================
 *
 * `ABOUT_SYSTEM_HANDOFF.md`, and it rules out the wrong answers by name:
 *
 *   · `package.json` is `0.0.0` and is NOT a product version. Never shown.
 *   · `schemaVersion` is a DATABASE schema version (18 here) and is not an application version.
 *     The handoff puts it in administrator detail with an explicit label, or nowhere.
 *   · *"Do not invent 1.0.0"*. If there is no agreed human-facing scheme, the RELEASE TAG is the
 *     version, because it is the only identifier that names the bytes that are running.
 *   · *"Missing metadata must display 'Version information unavailable', not fabricated values."*
 *
 * ── TWO IDENTITIES, AND THEY ARE ALLOWED TO DIFFER ──────────────────────────────────────────────
 *
 * The handoff: *"Keep frontend build identity and running API identity distinguishable. When they
 * differ, show an update/refresh notice rather than incorrectly asserting they are the same
 * release."*
 *
 * They differ for an ordinary reason: the interface is a bundle the browser cached, and the server
 * is whatever is deployed now. After a release the open tab is the OLD build talking to the NEW
 * API. Asserting one version in that moment would be wrong whichever one it named.
 *
 * The web build stamps itself at build time; the API states itself at runtime. When both are known
 * and unequal, this says so and tells the reader to refresh.
 *
 * ── AND IT DISCLOSES NOTHING OPERATIONAL ────────────────────────────────────────────────────────
 *
 * *"allowlist public fields; exclude builtBy usernames, local paths, secrets, internal addresses
 * and credentials."* What is shown is a tag, a short commit and an environment name. There is no
 * host, no path, no account and no connection string, and Copy Version Details copies exactly what
 * is on screen -- so what a person pastes into a support ticket is what they could already read.
 */

import { useCallback, useEffect, useState } from 'react';

import { callOperation } from '../api/operations';
import type { ReleaseOverview } from '../api/contract';
import { useLocale } from '../i18n/LocaleProvider';
import { ReleaseNotice } from './ReleaseNotice';

/*
 * THE BUILD STAMP, substituted by Vite at build time (vite.config.ts `define`).
 *
 * DECLARED HERE AND NOT IN A .d.ts. The `compiled-twins` guardrail forbids any .d.ts inside src/,
 * and the reason is sound: Node resolves an import to a .d.ts in preference to the .ts beside it,
 * which is how 44 build artifacts once made an entire suite assert against stale code. This is not
 * a module -- it is a literal substituted into the bundle -- so it belongs where it is read.
 *
 * An ordinary developer build substitutes an empty string, which reads as "not known".
 */
declare const __GLPOINT_WEB_RELEASE__: string;

/** Stamped into the bundle at build time; `null` when the build was not given one. */
const WEB_BUILD: string | null =
  typeof __GLPOINT_WEB_RELEASE__ === 'string' && __GLPOINT_WEB_RELEASE__.trim() !== ''
    ? __GLPOINT_WEB_RELEASE__
    : null;

export interface AboutSystemProps {
  readonly environment: string | null;
  readonly release: { readonly tag: string | null; readonly commit: string | null } | null;
  readonly onClose: () => void;
}

export function AboutSystem({ environment, release, onClose }: AboutSystemProps): JSX.Element {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const [update, setUpdate] = useState<ReleaseOverview | null>(null);

  /*
   * WHETHER A NEWER RELEASE HAS BEEN PUBLISHED, asked WHEN THIS DIALOG OPENS.
   *
   * Addendum 9.1(5) wants the Owner to learn about an update from the product. Three things about
   * the shape, each of which was a decision rather than a default:
   *
   *   IT IS ASKED HERE, NOT IN THE SHELL. A question asked on every page load, for every user, to
   *   answer a dialog almost nobody opens, is a request per session spent on nothing.
   *
   *   A FAILURE IS SILENCE, NOT AN ERROR. This read needs a platform capability, so a tenant user
   *   is refused -- which is correct and is not something to put on their screen. Anything that
   *   goes wrong leaves the state null and this section simply does not render.
   *
   *   AND IT INSTALLS NOTHING. The notice names the release and the approval that admitted it, and
   *   says where the installation is performed. A button here would be a browser performing a
   *   deployment, which is the one thing the release flow puts in the operator hands deliberately.
   */
  const readRelease = useCallback((): void => {
    void callOperation('platform.release.available.read', {}).then(
      (answer) => setUpdate(answer),
      () => setUpdate(null),
    );
  }, []);

  useEffect(() => {
    readRelease();
  }, [readRelease]);

  /*
   * ESCAPE CLOSES IT. ABOUT_SYSTEM_HANDOFF: *"keyboard accessible, with visible close action."*
   * The visible button is below; this is the half a keyboard user reaches for first, and a modal
   * that traps them is worse than one that never opened.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tag = release?.tag ?? null;
  const commit = release?.commit ?? null;

  /*
   * THE SAME WORDS AS THE RIBBON. Found in the first browser render: the ribbon said
   * "סביבת בדיקות" and this dialog said "staging", which reads as two different facts about one
   * deployment.
   *
   * The RAW value still goes into Copy Version Details below, because that text is pasted into a
   * support ticket where the literal configured string is the useful thing. Translated on screen,
   * literal in the clipboard -- two audiences, one fact.
   */
  const environmentLabel =
    environment === 'staging'
      ? t('env.staging')
      : environment === 'production'
        ? t('env.production')
        : environment === 'pilot'
          ? t('env.pilot')
          : t('env.unknown');

  /*
   * NOTHING KNOWN IS NOT "VERSION 0". The whole dialog collapses to one honest sentence, because a
   * dialog that showed an environment and a blank version would read as "version: (none)", which
   * is a claim about the version rather than an absence of one.
   */
  const known = tag !== null || commit !== null;

  const details = [
    `GL-POINT`,
    tag === null ? null : `${t('about.release')}: ${tag}`,
    commit === null ? null : `${t('about.build')}: ${commit.slice(0, 12)}`,
    `${t('about.environment')}: ${environment ?? t('env.unknown')}`,
    WEB_BUILD === null ? null : `web: ${WEB_BUILD}`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

  const copy = useCallback((): void => {
    void navigator.clipboard?.writeText(details).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }, [details]);

  // Both known and unequal. Unknown on either side is NOT a mismatch -- it is a thing we cannot
  // compare, and saying "refresh" on it would be guessing at a cause.
  const mismatch = WEB_BUILD !== null && tag !== null && WEB_BUILD !== tag;

  return (
    <div className="about" role="dialog" aria-modal="true" aria-label={t('about.title')}>
      <div className="about__panel">
        <h2 className="about__title">{t('about.title')}</h2>

        {known ? (
          <dl className="about__facts">
            <div className="about__fact">
              <dt>{t('about.release')}</dt>
              <dd>{tag ?? '—'}</dd>
            </div>
            <div className="about__fact">
              <dt>{t('about.build')}</dt>
              <dd>{commit === null ? '—' : commit.slice(0, 12)}</dd>
            </div>
            <div className="about__fact">
              <dt>{t('about.environment')}</dt>
              <dd>{environmentLabel}</dd>
            </div>
          </dl>
        ) : (
          <p className="about__unavailable">{t('about.unavailable')}</p>
        )}

        {mismatch ? <p className="about__mismatch">{t('about.buildMismatch')}</p> : null}

        {/*
          RENDERED BY ITS OWN COMPONENT, which takes the answer as a prop. That is what lets
          `releaseNotice.dom.test.tsx` drive every shape the server can return -- including each of
          the six reasons for having nothing to offer -- without a mock. This file performs the
          read; that one proves what a person sees.
        */}
        <ReleaseNotice release={update} onChanged={readRelease} />

        {/*
          RELEASE NOTES ARE NOT LINKED. The handoff: implement What's New only when notes for that
          exact version exist, otherwise say so -- *"never a dead link or invented notes."*
        */}
        <p className="about__notes">{t('about.releaseNotesUnavailable')}</p>

        {/*
          THE FONT LICENCE, WHERE A PERSON CAN ACTUALLY REACH IT.

          The SIL Open Font License requires the copyright notice and licence text to accompany
          every copy of the font, readable by the user. The file ships at this address and this is
          the link to it -- an attribution buried in a source comment satisfies a lawyer and nobody
          else.
        */}
        <p className="about__notes">
          <a href="/licenses/heebo-OFL.txt" target="_blank" rel="noreferrer">
            {t('about.fontLicence')}
          </a>
        </p>

        <div className="about__actions">
          <button type="button" className="btn" onClick={copy}>
            {copied ? t('about.copied') : t('about.copyDetails')}
          </button>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            {t('about.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
